import type { AxiosResponse } from 'axios';
import { ConfigStore } from '~/stores/config.store';
import { boundMethod } from 'autobind-decorator';
import { LoggerService } from '~/services/logger/logger.service';

export let REFRESH_TIMEOUT_MINIMUM_DURATION = 30 * 1000; // in milliseconds
export let REFRESH_TIMEOUT_MAXIMUM_DURATION = 4980 * 1000; // in milliseconds

export type VersionDiffUpdateData = {
    newApiVersion: number;
    isForced?: boolean;
    isReactVersion?: boolean;
};

export class GracefulService {
    private _appNeedsRefreshOnBlur: boolean = false;

    configStore: ConfigStore;
    timeoutId: NodeJS.Timeout;
    loggerService: LoggerService;

    constructor({ configStore }: { configStore: ConfigStore }) {
        this.configStore = configStore;
        this.loggerService = new LoggerService({
            featureName: 'gracefulReload',
            featureVersion: 1,
            messagePrefix: '[GracefulReload]'
        });
    }

    get appNeedsRefreshOnBlur() {
        return this._appNeedsRefreshOnBlur;
    }

    set appNeedsRefreshOnBlur(value: boolean) {
        // If the document is not focused, we should reload the app
        if (value && !document.hasFocus()) {
            this.reloadWithTimeout();
        }

        this._appNeedsRefreshOnBlur = value;
    }

    @boundMethod
    reload() {
        window.location.reload();
    }

    @boundMethod
    reloadTo(route: string) {
        window.location.href = `/app${route}`;
    }

    @boundMethod
    reloadWithTimeout() {
        this.cancelTimeout();

        const randomRefreshTimeout =
            Math.floor(
                Math.random() *
                    (REFRESH_TIMEOUT_MAXIMUM_DURATION -
                        REFRESH_TIMEOUT_MINIMUM_DURATION)
            ) + REFRESH_TIMEOUT_MINIMUM_DURATION;

        this.timeoutId = setTimeout(this.reload, randomRefreshTimeout);
    }

    @boundMethod
    cancelTimeout() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    @boundMethod
    validateAPIDiffOnRequestSuccess({ response }: { response: AxiosResponse }) {
        const serverApiVersion = this.getResponseApiVersion(response);

        this.reloadOnVersionDiff({ newApiVersion: serverApiVersion });
    }

    private getResponseApiVersion(response: AxiosResponse<any, any>) {
        const serviceApiVersion =
            response.headers[
                this.configStore.config.hb_api_headers.api_client_version.toLowerCase()
            ];

        return serviceApiVersion ? parseInt(serviceApiVersion) : null;
    }

    @boundMethod
    reloadOnVersionDiff({
        newApiVersion,
        isReactVersion,
        isForced
    }: VersionDiffUpdateData) {
        const { api_version, api_version_force_reload_diff } =
            this.configStore.config;
        const localApiVersion = api_version;

        if (newApiVersion && newApiVersion !== localApiVersion) {
            const apiDiff = newApiVersion - localApiVersion;
            if (isForced || apiDiff >= api_version_force_reload_diff) {
                this.loggerService.logInfo('user was gracefully reloaded', {
                    scope: 'react',
                    whatPublished: 'angular'
                });
                this.reload();

                return;
            }

            this.appNeedsRefreshOnBlur = true;

            return;
        }

        if (isReactVersion) {
            if (isForced) {
                this.loggerService.logInfo('user was gracefully reloaded', {
                    scope: 'react',
                    whatPublished: 'react'
                });

                this.reload();
            } else {
                this.appNeedsRefreshOnBlur = true;
            }
        }
    }
}
